import React, { useEffect, useState, useMemo } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { MultiSelect } from 'react-multi-select-component';
import Select from 'react-select';
import moment from 'moment';
import { isEmpty, set, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as todoAction from './store/Testing.action';
import { createOptions, Page_Size as PageSize, months, getYears, checkArray } from '../../../../Components/helper';
import { statusArr, excludeOptionsArr, dateOptions, customStyles } from './Constant';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import { CurrentYearItemRenderer } from '../../../../Components/Utility/CurrentYearDropdown';

function Filter() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');
  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: { userInfo: { userId }, isSupplierLoggedIn, isCompanyUserLoggedIn } = {},
    TestingTodoListData: {
      testingFilterValues: {
        selectedBrand,
        selectedSubBrand,
        selectedProductSupplier,
        selectedSeason,
        selectedMonth,
        selectedYear,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedTestingType,
        selectedBOM,
        selectedComponentSupplier,
        selectedColourRef,
        selectedStatus,
        selectedExpiry,
        selectedExclude,
        sortOrder,
        sortPropertyName,
        selectedStartDate,
        selectedEndDate,
        selectedProductType,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
      } = {},
      category1List,
      category2List,
      category3List,
      category4List,
      seasonList,
      brandList,
      userSuppliersList,
      suppliersList,
    } = {},
    ColorLibraryDetails: { ColorWaysLibList } = {},
    CostingTodoListData: { supplierBrandsList } = {},
    BomTypeDetails: { AllBomList } = {},
    SuppliersDetails: { tier2AndAboveList, tier1List } = {},
    SystemMastersDetails: { multiSubBrandList, pcTypeList,testTypeMaster = [], licensorList, licenseList, likenessList } = {},
  } = reducers || {};
  const testingTypeOptions = createOptions(testTypeMaster, 'name', '_id');
  const [state, setState] = useState({
    brandOptions: [],
    subBrandOptions: [],
    productSupplierOptions: [],
    seasonOptions: [],
    monthOptions: createOptions(months, 'name', '_id'),
    yearOptions: createOptions(getYears(), 'name', '_id'),
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    BOMOptions: [],
    componentSupplierOptions: [],
    colourRefOptions: [],
    statusOptions: createOptions(statusArr, 'name', '_id'),
    expiryOptions: createOptions(dateOptions, 'name', '_id'),
    excludeOptions: createOptions(excludeOptionsArr, 'name', '_id'),
    productTypeOptions: [],
  });
  const {
    brandOptions,
    subBrandOptions,
    productSupplierOptions,
    seasonOptions,
    monthOptions,
    yearOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    BOMOptions,
    componentSupplierOptions,
    colourRefOptions,
    statusOptions,
    expiryOptions,
    excludeOptions,
    productTypeOptions,
  } = state;

  const [isApply, setIsApply] = useState(false);
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('testingTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0 || parsed?.selectedLicense?.length > 0 || parsed?.selectedLikeness?.length > 0) {
        dispatch(todoAction.filterSortData({
          selectedLicensor: parsed.selectedLicensor || [],
          selectedLicense: parsed.selectedLicense || [],
          selectedLikeness: parsed.selectedLikeness || [],
        }));
      }
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({
          licensorIds: parsed.selectedLicensor,
          loggedInUserId: userId,
          isActive: true,
        }));
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(systemMasterAction.getLikenessDetails({
          licensorIds: parsed.selectedLicensor,
          licenseIds: parsed.selectedLicense,
          loggedInUserId: userId,
          isActive: true,
        }));
      }
    } catch (e) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  // Fetch feature flag for cascading by Product Type
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  // Ensure Product Types (Tier1) are available for the Product Type MultiSelect
  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  // Build product type options locally from store list
  useEffect(() => {
    if (Array.isArray(pcTypeList) && pcTypeList?.length > 0) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') || [] }));
    } else {
      setState((prev) => ({ ...prev, productTypeOptions: [] }));
    }
  }, [pcTypeList]);

  // Helpers for cascading filtering (same as reference file)
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);
  const selectedPTIds = getIds(selectedProductType);
  const selectedCat1Ids = getIds(selectedCategory1);
  const selectedCat2Ids = getIds(selectedCategory2);
  const selectedCat3Ids = getIds(selectedCategory3);

  // Client-side filtering using useMemo (same approach as reference file)
  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds?.includes(id)) || pts?.some((id) => selectedPTIds?.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt?.isUsedCat1?.length > 0);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
      return usedCat1?.some((id) => selectedCat1Ids?.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids?.includes(opt?.categoryOneId) && selectedCat2Ids?.includes(opt?.categoryTwoId));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) =>
        selectedCat1Ids?.includes(opt?.categoryOneId) && selectedCat2Ids?.includes(opt?.categoryTwoId) && selectedCat3Ids?.includes(opt?.categoryThreeId),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    selectedDate: null,
    isShowDatePicker: false,
  });

  useEffect(() => {
    const brandId = selectedBrand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selectedBrand]);

  // Map reducer subBrand list to dropdown options
  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList?.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts || [] }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  useEffect(() => {
    try {
      const isTrue = JSON.parse(localStorage.getItem('testingTodoListFilter') || 'null');
      if (isTrue) {
        if (isTrue?.selectedExpiry && isTrue.selectedExpiry === 'customDates') {
          setDateRange({
            startDate: isTrue?.selectedStartDate ? new Date(isTrue.selectedStartDate) : null,
            endDate: isTrue?.selectedEndDate ? new Date(isTrue.selectedEndDate) : null,
            selectedDate: expiryOptions?.filter((option) => option?._id === isTrue?.selectedExpiry)?.[0] || null,
            isShowDatePicker: isTrue?.selectedExpiry === 'customDates',
          });
        }
        const hasTruthyValue = Object.values(isTrue || {}).some((value) => Array.isArray(value) && value?.length > 0);
        if (hasTruthyValue) {
          setIsApply(true);
        }
      }
    } catch (e) {
      console.error('Error parsing localStorage filter:', e);
    }
  }, []);

  const handleSelectChange = (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);

    // When Product Type changes and cascading is ON, clear all downstream category selections
    if (name === 'selectedProductType' && catBasedOnPTData) {
      obj.selectedCategory1 = [];
      obj.selectedCategory2 = [];
      obj.selectedCategory3 = [];
      obj.selectedCategory4 = [];
    }

    const resetCategories = ['selectedCategory1', 'selectedCategory2'];

    if (resetCategories.includes(name)) {
      obj.selectedCategory3 = [];
      obj.selectedCategory4 = [];
    } else if (name === 'selectedCategory3') {
      obj.selectedCategory4 = [];
    }

    if (
      ['selectedBrand'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }

    dispatch(todoAction.filterSortData(obj));
  };

  const licensorOptions = useMemo(() => checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : [], [licensorList]);
  const licenseOptions = useMemo(() => checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : [], [licenseList]);
  const likenessOptions = useMemo(() => checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : [], [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(todoAction.filterSortData(newData));
  };

  const clearAll = async () => {
    const paramsObj = {
      seasons: [],
      year: [],
      brand: [],
      subBrand: [],
      supplier: [],
      colourRef: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      productTypeId: [],
      status: {},
      pageIndex: 1,
      pageSize: PageSize,
      type: 'testing',
    };
    const sortinObj = {
      selectedBrand: [],
      selectedSubBrand: [],
      selectedProductSupplier: [],
      selectedSeason: [],
      selectedMonth: [],
      selectedYear: [],
      selectedCategory1: [],
      selectedCategory2: [],
      selectedCategory3: [],
      selectedCategory4: [],
      selectedProductType: [],
      selectedTestingType: [],
      selectedBOM: [],
      selectedComponentSupplier: [],
      selectedColourRef: [],
      selectedStatus: [],
      selectedExpiry: [],
      selectedExclude: [],
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: PageSize,
      selectedStartDate: '',
      selectedEndDate: '',
    };
    localStorage.removeItem('testingTodoListFilter');
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    setDateRange({
      startDate: null,
      endDate: null,
      selectedDate: null,
      isShowDatePicker: false,
    });
    const res = await dispatch(todoAction.getTestingList(paramsObj));
    setIsApply(false);
    if (res) dispatch(todoAction.filterSortData(sortinObj));
  };

  const applyFilter = async () => {
    setIsApply(true);
    const paramsObj = {
      month: selectedMonth,
      year: selectedYear,
      seasons: selectedSeason,
      brand: selectedBrand,
      subBrand: selectedSubBrand,
      productSupplier: selectedProductSupplier,
      category1: selectedCategory1,
      category2: selectedCategory2,
      category3: selectedCategory3,
      category4: selectedCategory4,
      productTypeId: Array.isArray(selectedProductType) ? selectedProductType : [],
      testingType: selectedTestingType,
      BOMCompoent: selectedBOM,
      componentSupplier: selectedComponentSupplier,
      colourRef: selectedColourRef,
      status: selectedStatus,
      expiry: selectedExpiry,
      exclude: selectedExclude,
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: PageSize,
      type: 'testing',
      startDate: selectedStartDate,
      endDate: selectedEndDate,
    };
    const paramsObj1 = {
      selectedBrand,
      selectedSubBrand,
      selectedProductSupplier,
      selectedSeason,
      selectedMonth,
      selectedYear,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
      selectedProductType,
      selectedTestingType,
      selectedBOM,
      selectedComponentSupplier,
      selectedColourRef,
      selectedStatus,
      selectedExpiry,
      selectedExclude,
      selectedStartDate,
      selectedEndDate,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('testingTodoListFilter', JSON.stringify(paramsObj1));
    dispatch(todoAction.getTestingList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: PageSize,
    };
    dispatch(todoAction.filterSortData(obj));
  };

  useEffect(() => {
    if (isSupplierLoggedIn || supplierBrandsList || brandList) {
      setState((prevState) => ({
        ...prevState,
        brandOptions: createOptions(isSupplierLoggedIn ? supplierBrandsList : brandList, 'name', '_id') || [],
      }));
    }
    if (seasonList) {
      setState((prevState) => ({
        ...prevState,
        seasonOptions: createOptions(seasonList, 'name', '_id') || [],
      }));
    }
    if (category1List) {
      setState((prevState) => ({
        ...prevState,
        category1Options: createOptions(category1List, 'name', '_id') || [],
      }));
    }
    if (category2List) {
      setState((prevState) => ({
        ...prevState,
        category2Options: createOptions(category2List, 'name', '_id') || [],
      }));
    }
    if (ColorWaysLibList) {
      setState((prevState) => ({
        ...prevState,
        colourRefOptions: createOptions(ColorWaysLibList, 'name', '_id') || [],
      }));
    }
    if (AllBomList) {
      setState((prevState) => ({
        ...prevState,
        BOMOptions: createOptions(AllBomList, 'referenceCode', '_id') || [],
      }));
    }
    if (tier1List || tier2AndAboveList) {
      const suppArray = [...(Array.isArray(tier1List) ? tier1List : []), ...(Array.isArray(tier2AndAboveList) ? tier2AndAboveList : [])];
      const upnewArr = uniqBy(suppArray, (e) => e?._id);
      setState((prevState) => ({
        ...prevState,
        componentSupplierOptions: createOptions(upnewArr, 'name', '_id') || [],
      }));
    }
  }, [isSupplierLoggedIn, seasonList, brandList, category1List, category2List, supplierBrandsList, ColorWaysLibList, AllBomList, tier2AndAboveList, tier1List]);

  useEffect(() => {
    if (checkArray(category3List)) {
      setState((prevState) => ({
        ...prevState,
        category3Options: createOptions(category3List, 'name', '_id') || [],
      }));
    }
  }, [category3List]);

  useEffect(() => {
    if (checkArray(category4List)) {
      setState((prevState) => ({
        ...prevState,
        category4Options: createOptions(category4List, 'name', '_id') || [],
      }));
    }
  }, [category4List]);

  useEffect(() => {
    if (!isSupplierLoggedIn) {
      const suppList = isCompanyUserLoggedIn
        ? (userSuppliersList?.suppliers || []).map((ele) => ({
            ...ele,
            name: Array.isArray(ele?.companies) && ele?.companies?.length > 0 ? ele?.companies?.[0]?.supplierName : ele?.name,
          }))
        : suppliersList;

      if (checkArray(suppList)) {
        const supplierListOptions = createOptions(suppList, 'name', '_id') || [];
        setState((prevState) => ({
          ...prevState,
          productSupplierOptions: supplierListOptions,
        }));
      }
    }
  }, [userSuppliersList, suppliersList, isCompanyUserLoggedIn, isSupplierLoggedIn]);

  useEffect(() => {
    const callFn = () => {
      if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && checkArray(selectedCategory3) && !isEmpty(userId)) {
        dispatch(
          todoAction.fetchCategoryAction4({
            accessedFrom: 'masters',
            categoryType: 4,
            loggedInUserId: userId,
            categoryOneId: selectedCategory1,
            categoryTwoId: selectedCategory2,
            categoryThreeId: selectedCategory3,
            isActive: 1,
          }),
        );
      }
      if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && !isEmpty(userId)) {
        dispatch(
          todoAction.fetchCategoryAction3({
            accessedFrom: 'masters',
            categoryType: 3,
            loggedInUserId: userId,
            categoryOneId: selectedCategory1,
            categoryTwoId: selectedCategory2,
            isActive: 1,
          }),
        );
      }
    };
    callFn();
  }, [selectedCategory1, selectedCategory2, selectedCategory3, dispatch, userId]);

  const onChangeFilter = (selected, filterName) => {
    if (!Array.isArray(selected)) {
      return handleSelectChange([], filterName);
    }
    return handleSelectChange(selected.map((val) => val?._id).filter(Boolean), filterName);
  };

  const getSelectedVal = (option, selected) => {
    if (!Array.isArray(option) || !Array.isArray(selected)) {
      return [];
    }
    return option.filter((val) => !isEmpty(selected) && selected.includes(val?._id));
  };

  const filterData = [
    {
      label: t('COSTING_TO_DO_LIST.BRAND'),
      onChange: (selected) => onChangeFilter(selected, 'selectedBrand'),
      selectedValue: getSelectedVal(brandOptions, selectedBrand),
      optionList: brandOptions,
      name: 'brand',
    },
    subBrandAccess && {
      label: t('COSTING_TO_DO_LIST.SUBBRAND'),
      onChange: (selected) => onChangeFilter(selected, 'selectedSubBrand'),
      selectedValue: getSelectedVal(subBrandOptions, selectedSubBrand),
      optionList: subBrandOptions,
      name: 'subbrand',
    },

    {
      label: t('TAKING_DATES.PRODUCT_SUPLIER'),
      onChange: (selected) => onChangeFilter(selected, 'selectedProductSupplier'),
      selectedValue: getSelectedVal(productSupplierOptions, selectedProductSupplier),
      optionList: productSupplierOptions,
      name: 'product supplier',
    },
    {
      label: t('COSTING_TO_DO_LIST.SEASON'),
      onChange: (selected) => onChangeFilter(selected, 'selectedSeason'),
      selectedValue: getSelectedVal(seasonOptions, selectedSeason),
      optionList: seasonOptions,
      name: 'season',
    },
    {
      label: t('PROGRESS_INDICATOR.MONTH'),
      onChange: (selected) => onChangeFilter(selected, 'selectedMonth'),
      selectedValue: getSelectedVal(monthOptions, selectedMonth),
      optionList: monthOptions,
      name: 'month',
    },

    {
      label: t('COSTING_TO_DO_LIST.YEAR'),
      onChange: (selected) => onChangeFilter(selected, 'selectedYear'),
      selectedValue: getSelectedVal(yearOptions, selectedYear),
      optionList: yearOptions,
      name: 'year',
    },
    catBasedOnPTData && {
      label: 'Product Type',
      onChange: (selected) => onChangeFilter(selected, 'selectedProductType'),
      selectedValue: getSelectedVal(productTypeOptions, selectedProductType),
      optionList: productTypeOptions,
      name: 'product type',
    },
    {
      label: t('SPEC_TEMP_LIBRARY.CATEGORY1'),
      onChange: (selected) => onChangeFilter(selected, 'selectedCategory1'),
      selectedValue: getSelectedVal(catBasedOnPTData ? category1Filtered : category1Options, selectedCategory1),
      optionList: catBasedOnPTData ? category1Filtered : category1Options,
      name: 'category 1',
    },
    {
      label: t('SPEC_TEMP_LIBRARY.CATEGORY2'),
      onChange: (selected) => onChangeFilter(selected, 'selectedCategory2'),
      selectedValue: getSelectedVal(catBasedOnPTData ? category2Filtered : category2Options, selectedCategory2),
      optionList: catBasedOnPTData ? category2Filtered : category2Options,
      name: 'category 2',
    },
    {
      label: t('SPEC_TEMP_LIBRARY.CATEGORY3'),
      onChange: (selected) => onChangeFilter(selected, 'selectedCategory3'),
      selectedValue: getSelectedVal(catBasedOnPTData ? category3Filtered : category3Options, selectedCategory3),
      optionList: catBasedOnPTData ? category3Filtered : category3Options,
      name: 'category 3',
    },
    {
      label: t('COSTING_TO_DO_LIST.CATEGORY_4'),
      onChange: (selected) => onChangeFilter(selected, 'selectedCategory4'),
      selectedValue: getSelectedVal(catBasedOnPTData ? category4Filtered : category4Options, selectedCategory4),
      optionList: catBasedOnPTData ? category4Filtered : category4Options,
      name: 'category 4',
    },
    {
      label: t('TAKING_DATES.TESTING_TYPE'),
      onChange: (selected) => onChangeFilter(selected, 'selectedTestingType'),
      selectedValue: getSelectedVal(testingTypeOptions, selectedTestingType),
      optionList: testingTypeOptions,
      name: 'testing type',
    },
    {
      label: t('TAKING_DATES.BOM_COMPONENT'),
      onChange: (selected) => onChangeFilter(selected, 'selectedBOM'),
      selectedValue: getSelectedVal(BOMOptions, selectedBOM),
      optionList: BOMOptions,
      name: 'BOM component',
    },
    {
      label: t('TAKING_DATES.COMPONENT_SUPPLIER'),
      onChange: (selected) => onChangeFilter(selected, 'selectedComponentSupplier'),
      selectedValue: getSelectedVal(componentSupplierOptions, selectedComponentSupplier),
      optionList: componentSupplierOptions,
      name: 'component supplier',
    },
    {
      label: t('TAKING_DATES.COLOR_REF'),
      onChange: (selected) => onChangeFilter(selected, 'selectedColourRef'),
      selectedValue: getSelectedVal(colourRefOptions, selectedColourRef),
      optionList: colourRefOptions,
      name: 'colour ref',
    },

    {
      label: t('COSTING_TO_DO_LIST.STATUSS'),
      onChange: (selected) => onChangeFilter(selected, 'selectedStatus'),
      selectedValue: getSelectedVal(statusOptions, selectedStatus),
      optionList: statusOptions,
      name: 'status',
    },
  ];

  const handleSelectDate = (select) => {
    if (select) {
      setDateRange((prevState) => ({
        ...prevState,
        selectedDate: select,
        isShowDatePicker: select?._id === 'customDates',
      }));

      const currentDate = moment();
      let start;
      let end;
      const startFormat = 'YYYY-MM-DD[T00:00:00.000Z]';
      const endFormat = 'YYYY-MM-DD[T23:59:59.999Z]';

      switch (select?._id) {
        case 'today':
          start = currentDate.format(startFormat);
          end = currentDate.format(endFormat);
          break;
        case 'thisWeek': {
          const weekStart = currentDate.clone().startOf('week');
          const weekEnd = currentDate.clone().endOf('week');
          start = weekStart.format(startFormat);
          end = weekEnd.format(endFormat);
          break;
        }
        case 'thisMonth': {
          const monthStart = currentDate.clone().startOf('month');
          const monthEnd = currentDate.clone().endOf('month');
          start = monthStart.format(startFormat);
          end = monthEnd.format(endFormat);
          break;
        }
        default:
        // Handle other cases if needed
      }

      handleSelectChange(start, 'selectedStartDate');
      handleSelectChange(end, 'selectedEndDate');
      handleSelectChange(select?._id, 'selectedExpiry');
    } else {
      handleClearDates();
    }
  };

  const onchangeDate = (dates) => {
    const [start, end] = dates;
    setDateRange((prevState) => ({
      ...prevState,
      startDate: start,
      endDate: end,
    }));
    if (start && end) {
      handleSelectChange(start, 'selectedStartDate');
      handleSelectChange(end, 'selectedEndDate');
      handleSelectChange('customDates', 'selectedExpiry');
    }
  };

  const handleClearDates = () => {
    setDateRange({
      startDate: null,
      endDate: null,
      selectedDate: null,
      isShowDatePicker: false,
    });
    handleSelectChange('', 'selectedStartDate');
    handleSelectChange('', 'selectedEndDate');
    handleSelectChange([], 'selectedExpiry');
  };

  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('TAKING_DATES.SUBMIT_FILTER')}
    >
      <OverlayTrigger
        placement={t('TAKING_DATES.BUTTON')}
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  {filterData
                    .filter((data) => {
                      if (!data) return false;
                      if (isSupplierLoggedIn) {
                        return data?.name !== 'component supplier';
                      }
                      return data;
                    })
                    .map((data) => (
                      <FormGroup className="mb-2" key={data?.label}>
                        <div className="custom-react-select ">
                          <MultiSelect
                            hasSelectAll
                            name={data?.name}
                            ItemRenderer={data?.name === 'year' ? CurrentYearItemRenderer : undefined}
                            options={data?.optionList || []}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={data?.selectedValue || []}
                            overrideStrings={{
                              selectSomeItems: `Select ${data?.label || ''}`,
                            }}
                            labelledBy={`Select ${data?.label || ''}`}
                            onChange={(selected) => {
                              data?.onChange?.(selected);
                            }}
                          />
                        </div>
                      </FormGroup>
                    ))}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      {!dateRange.isShowDatePicker ? (
                        <Select
                          isClearable="true"
                          classNamePrefix="common-select"
                          name="repoort expiry dates"
                          options={expiryOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          getOptionLabel={(option) => `${option.label}`}
                          getOptionValue={(option) => `${option.value}`}
                          value={dateRange?.selectedDate ? { label: dateRange.selectedDate?.label, value: dateRange.selectedDate?.value } : null}
                          onChange={(selected) => {
                            handleSelectDate(selected);
                          }}
                          placeholder={t('TAKING_DATES.SELECT_EXPIRY_DATES')}
                          styles={customStyles}
                        />
                      ) : (
                        <div>
                          <CustomDatePicker
                            dateFormat="dd-MMM-yyyy"
                            valueProp={dateRange.startDate}
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            popperPlace="top-end"
                            name="selectedDate"
                            isRange
                            onChange={onchangeDate}
                            placeholder={t('TAKING_DATES.SELECT_EXPIRY_DATES')}
                            style={{ height: '24px', width: '100%', border: 'none', borderBottom: '1px solid #848484', borderRadius: 0 }}
                          />
                        </div>
                      )} 
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="exclude"
                        overrideStrings={{
                          selectSomeItems: t('TAKING_DATES.SELECT_EXCLUDE'),
                        }}
                        labelledBy={t('TAKING_DATES.SELECT_EXCLUDE')}
                        options={excludeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={
                          Array.isArray(selectedExclude) && Array.isArray(excludeOptions)
                            ? excludeOptions.filter((val) => !isEmpty(selectedExclude) && selectedExclude.includes(val?._id))
                            : []
                        }
                        onChange={(selected) =>
                          handleSelectChange(Array.isArray(selected) ? selected.map((val) => val?._id).filter(Boolean) : [], 'selectedExclude')
                        }
                      />
                    </div>
                  </FormGroup>
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="licensor"
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) => handleFilterLicense(selected.map((val) => val._id), 'selectedLicensor')}
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="license"
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) => handleFilterLicense(selected.map((val) => val._id), 'selectedLicense')}
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="likeness"
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LIKENESS'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) => handleFilterLicense(selected.map((val) => val._id), 'selectedLikeness')}
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>
                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default Filter;
